/**
 * The starting surface for map blocks reached through a fence.
 *
 * `Cmarkdownmap` already takes a `defaultExpanded`, and a direct embedder —
 * `CresultcardMap`, say — simply passes it. A block that arrives through
 * `Cmarkdown` cannot: the block renderer constructs `<Cmarkdownmap content={…}/>`
 * from the fence and nothing else, by design, because a markdown fence carries
 * content and not presentation. So an embedder that renders REAL fences and
 * wants them open had exactly two options before this file, and both were bad:
 * call `Cmarkdownmap` directly and skip fence sniffing, `parseMapBlock` and
 * surface selection — the path most worth exercising — or reach into the DOM
 * after mount and synthesise a click on the shell's expand control.
 *
 * The click was the one in place, and it failed in two ways that matter. It
 * addressed the control by its accessible name, so renaming that string would
 * have broken the surface silently, with nothing to typecheck. And it produced a
 * genuine expand TRANSITION rather than an initial state, so `Cmarkdownvisual`
 * centred the freshly-opened block in its scroller and the page jumped on mount.
 *
 * Context carries the intent instead, the way `cartography-ink.context` carries
 * the ink: the default is `undefined`, meaning "every existing embedder keeps
 * the collapsed tile it has always had", and a provider is an override for the
 * one surface that wants something else. An explicit `defaultExpanded` prop
 * still wins over the context — the prop is the specific instruction and the
 * context is the ambient one.
 */

'use client';

import { createContext, useContext, type ReactElement, type ReactNode } from 'react';

/** `undefined` means "no embedder has an opinion", which is what keeps the
 *  shipped collapsed tile the behaviour for every call site that mounts no
 *  provider. It is deliberately not `false`: absence and "explicitly closed"
 *  are the same rendering today, but only one of them is a decision. */
const MapExpansionContext = createContext<boolean | undefined>(undefined);

interface CmarkdownmapExpansionProviderProps {
  /** The starting surface for every fenced map block below this point. */
  defaultExpanded: boolean;
  children: ReactNode;
}

export function CmarkdownmapExpansionProvider({
  defaultExpanded,
  children,
}: CmarkdownmapExpansionProviderProps): ReactElement {
  return (
    <MapExpansionContext.Provider value={defaultExpanded}>
      {children}
    </MapExpansionContext.Provider>
  );
}

export function useMapDefaultExpanded(): boolean | undefined {
  return useContext(MapExpansionContext);
}
